/**
 * What the signed-in user may open, from `/me` grants. This only hides navigation the user
 * cannot use; the server still checks every call. While `/me` loads, or if it fails, nothing is
 * hidden (the pages themselves report a refused call).
 */

import { useMemo, useSyncExternalStore } from 'react';
import { api, type Me, type StaffGrant } from '../api';
import { useSession } from '../api/session';
import type { HubDef, HubSubDef, HubTabDef } from '../nav/hubs';

/** Roles held by customer accounts rather than staff. */
const CLIENT_ROLES = new Set(['customer']);

let cache: { token: string; me: Me | null } | null = null;
let pending: string | null = null;
const listeners = new Set<() => void>();

function load(token: string): void {
  if (pending === token || cache?.token === token) return;
  pending = token;
  api.me().then(
    (me) => {
      if (pending !== token) return;
      cache = { token, me };
      pending = null;
      for (const l of listeners) l();
    },
    () => {
      if (pending !== token) return;
      cache = { token, me: null };
      pending = null;
      for (const l of listeners) l();
    },
  );
}

function useMe(): Me | null {
  const token = useSession()?.token ?? '';
  const snapshot = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => (cache && cache.token === token ? cache.me : null),
  );
  if (token && cache?.token !== token) load(token);
  return snapshot;
}

export interface Access {
  /** Signed in as a customer user: only their hubs and tabs are shown. */
  client: boolean;
  /** The `/me` grants; `null` while they load or when `/me` failed. */
  grants: readonly StaffGrant[] | null;
  /** The signed-in user; `null` while `/me` loads or when it failed. */
  userId: string | null;
  /** True when no permission is required or the user holds one of `perms`. */
  has: (perms?: readonly string[]) => boolean;
  tab: (tab: HubTabDef | HubSubDef) => boolean;
  /** Visible tabs of a hub, each with its visible sub-tabs. */
  tabs: (hub: HubDef) => HubTabDef[];
}

export function accessFor(me: Me | null): Access {
  const perms = new Set(me?.grants.flatMap((g) => g.permissions) ?? []);
  const known = Boolean(me && me.grants.length);
  const client = known && (me?.grants ?? []).every((g) => CLIENT_ROLES.has(g.role) || /customer/i.test(g.scope));
  const has = (p?: readonly string[]) => !known || !p || p.length === 0 || p.some((x) => perms.has(x));
  const tab = (x: HubTabDef | HubSubDef) => (!client || x.client === true) && has(x.perms);
  const tabs = (hub: HubDef) =>
    hub.tabs.filter(tab).flatMap((x) => {
      if (!x.subs) return [x];
      const subs = x.subs.filter((s) => (!client || s.client === true) && has(s.perms));
      return subs.length ? [{ ...x, subs }] : [];
    });
  return { client, grants: me?.grants ?? null, userId: me?.user_id ?? null, has, tab, tabs };
}

export function useAccess(): Access {
  const me = useMe();
  return useMemo(() => accessFor(me), [me]);
}
